import { Controller } from 'stimulus'

const BLOCK_TAGS = ['DIV', 'P', 'LI']
const TOKEN      = /\{\{\s*(.+?)\s*\}\}/g
const ESCAPED    = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

export default class extends Controller {

  static targets = ['field', 'group', 'kind', 'input', 'editor', 'labels', 'panel']

  connect() {
    if (this.hasEditorTarget) this.startEditor()

    this.activeField = this.hasEditorTarget
      ? this.editorTarget
      : this.fieldTargets.find(field => field.tagName === 'TEXTAREA') || this.fieldTargets[0]

    // The panel sits under the field it fills in, so having every panel out at
    // once pushed the whole page down: one only comes out while its field is
    // being written in.
    this._onFocusIn  = event => this.showPanel(this.writesTokens(event.target))
    this._onFocusOut = event => { if (!this.writesTokens(event.relatedTarget)) this.showPanel(false) }

    this.element.addEventListener('focusin',  this._onFocusIn)
    this.element.addEventListener('focusout', this._onFocusOut)

    this.toggle()
  }

  disconnect() {
    this.element.removeEventListener('focusin',  this._onFocusIn)
    this.element.removeEventListener('focusout', this._onFocusOut)
  }

  showPanel(visible) {
    if (this.hasPanelTarget) this.panelTarget.hidden = !visible
  }

  // The panel belongs to the fields it fills in, and to itself: a click on one of
  // its buttons must not read as the field being left.
  writesTokens(node) {
    if (!node) return false
    if (this.hasPanelTarget  && this.panelTarget.contains(node))  return true
    if (this.hasEditorTarget && this.editorTarget.contains(node)) return true

    return this.fieldTargets.includes(node)
  }

  toggle() {
    if (!this.hasKindTarget) return

    const kind = this.kindTarget.value
    this.groupTargets.forEach(group => {
      group.hidden = !group.dataset.kinds.split(' ').includes(kind)
    })
  }

  focusField(event) {
    this.activeField = event.currentTarget
  }

  // Keeps the caret where it is: without this the button takes the focus and
  // the placeholder lands at the start of the text instead of at the cursor.
  keepFocus(event) {
    event.preventDefault()
  }

  insert(event) {
    const token = event.currentTarget.dataset.token

    if (this.hasEditorTarget && this.activeField === this.editorTarget) {
      this.insertPill(token)
    } else {
      this.insertText(token)
    }
  }

  startEditor() {
    this.editorTarget.innerHTML = this.pillify(this.inputTarget.value)
    this.editorTarget.hidden = false
    this.inputTarget.classList.add('d-none')
  }

  sync() {
    this.inputTarget.value = this.serialize(this.editorTarget)
  }

  editorClick(event) {
    const button = event.target.closest('.token-pill__remove')
    if (!button) return

    button.closest('.token-pill').remove()
    this.sync()
  }

  insertPill(token) {
    const holder = document.createElement('div')
    holder.innerHTML = this.pillHtml(token)

    this.insertNodes([holder.firstChild, document.createTextNode(' ')])
    this.sync()
  }

  insertText(token) {
    const field = this.activeField
    if (!field) return

    const start = field.selectionStart === null ? field.value.length : field.selectionStart
    const end   = field.selectionEnd   === null ? field.value.length : field.selectionEnd

    field.value = field.value.slice(0, start) + token + field.value.slice(end)

    const caret = start + token.length
    field.focus()
    field.setSelectionRange(caret, caret)
    field.dispatchEvent(new Event('input'))
  }

  insertNodes(nodes) {
    const selection = window.getSelection()
    let range

    if (selection.rangeCount && this.editorTarget.contains(selection.anchorNode)) {
      range = selection.getRangeAt(0)
      range.deleteContents()
    } else {
      range = document.createRange()
      range.selectNodeContents(this.editorTarget)
      range.collapse(false)
    }

    nodes.forEach(node => {
      range.insertNode(node)
      range.setStartAfter(node)
    })
    range.collapse(true)

    selection.removeAllRanges()
    selection.addRange(range)
    this.editorTarget.focus()
  }

  pillify(text) {
    let html = ''
    let read = 0
    let match

    TOKEN.lastIndex = 0
    while ((match = TOKEN.exec(text)) !== null) {
      html += this.escape(text.slice(read, match.index)) + this.pillHtml(match[0])
      read = TOKEN.lastIndex
    }

    return html + this.escape(text.slice(read))
  }

  // A placeholder nobody offers — hand written, or left over from a document
  // that no longer applies — still shows as a pill, under its raw expression.
  pillHtml(token) {
    const expression = token.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '')
    const label      = this.labels[expression] || expression

    return `<span class="token-pill" contenteditable="false" data-token="${this.escape(token)}">` +
             `${this.escape(label)}` +
             `<button type="button" class="token-pill__remove" tabindex="-1" title="${this.escape(this.removeLabel)}">&times;</button>` +
           `</span>`
  }

  serialize(node) {
    let text = ''

    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.nodeValue
      } else if (child.nodeType !== Node.ELEMENT_NODE) {
      } else if (child.classList.contains('token-pill')) {
        text += child.dataset.token
      } else if (child.tagName === 'BR') {
        text += '\n'
      } else if (BLOCK_TAGS.includes(child.tagName)) {
        if (text && !text.endsWith('\n')) text += '\n'
        text += this.serialize(child)
      } else {
        text += this.serialize(child)
      }
    })

    return text
  }

  escape(text) {
    return String(text).replace(/[&<>"']/g, character => ESCAPED[character])
  }

  get labels() {
    if (!this._labels) {
      this._labels = this.hasLabelsTarget ? JSON.parse(this.labelsTarget.dataset.labels || '{}') : {}
    }

    return this._labels
  }

  get removeLabel() {
    return this.hasEditorTarget ? this.editorTarget.dataset.removeLabel || '' : ''
  }
}
